/* eslint-disable react-refresh/only-export-components */
/**
 * AuthContext: fuente única del estado de sesión.
 *
 * - Guarda/lee el token y el user en `sessionStorage` (ver `@shared/auth/sesion`):
 *   sobrevive a recargas pero no al cierre del navegador (nota de seguridad:
 *   en Fase 5 se propondrá migrar a httpOnly cookies; requiere backend).
 * - Al entrar y al salir vacía la caché de react-query: sin esto, el
 *   siguiente usuario de la misma pestaña veía unos minutos los datos
 *   cacheados del anterior (p. ej. sus avisos en la campana).
 * - Inyecta un handler de 401 en el httpClient para poder navegar con
 *   React Router en lugar de `window.location.href`.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';

import { queryClient } from '@app/queryClient';
import { setUnauthorizedHandler } from '@shared/api/interceptors';
import {
  borrarSesion,
  guardarSesion,
  leerToken,
  leerUsuario,
  purgarSesionAntigua,
} from '@shared/auth/sesion';

export interface AuthUser {
  id: number;
  name: string;
  email: string;
  role: string;
}

interface AuthContextValue {
  user: AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;
  login: (user: AuthUser, token: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [user, setUser] = useState<AuthUser | null>(() => {
    purgarSesionAntigua();
    return leerUsuario<AuthUser>();
  });
  const [token, setToken] = useState<string | null>(() => leerToken());

  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    borrarSesion();
    queryClient.clear();
    navigate('/login', { replace: true });
  }, [navigate]);

  const login = useCallback((nextUser: AuthUser, nextToken: string) => {
    queryClient.clear();
    setUser(nextUser);
    setToken(nextToken);
    guardarSesion(nextToken, nextUser);
  }, []);

  // Conectar handler 401 al httpClient para usar react-router.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      logout();
    });
  }, [logout]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      token,
      isAuthenticated: Boolean(token),
      login,
      logout,
    }),
    [user, token, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth debe usarse dentro de <AuthProvider>');
  }
  return ctx;
}
